import { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { isNative, pickImageFile } from "@/lib/native";
import { useLang } from "@/context/LangContext";
import { EVENT_CATEGORIES, JOB_CATEGORIES } from "@/lib/i18n";
import { Calendar, Briefcase, Upload, X, Image as ImageIcon } from "lucide-react";
import { EventFormatFields } from "@/components/events/EventFormatFields";
import { TicketingSection, toTicketingBody, blankType, toLocalInput } from "@/components/events/TicketingSection";
import { DeleteEventButton } from "@/components/events/DeleteEventButton";

const TYPES = [
  { id: "event", label: "Evento", icon: Calendar, endpoint: "/events" },
  { id: "job", label: "Emprego", icon: Briefcase, endpoint: "/jobs" },
];

export default function Publish() {
  const [params] = useSearchParams();
  const [type, setType] = useState(TYPES.some((x) => x.id === params.get("type")) ? params.get("type") : "event");
  const { t } = useLang();
  const nav = useNavigate();
  const editId = params.get("edit");
  const [form, setForm] = useState(editId ? null : { format: "in_person", timezone: "Europe/London" });
  const [tk, setTk] = useState({ choice: "none", capacity: "", end_time: "", types: [] });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    Promise.all([api.get(`/events/${editId}/manage`), api.get(`/events/${editId}/tickets`)]).then(([ev, tt]) => {
      const e = ev.data; setType("event");
      const choice = e.ticketing_choice || (e.ticket_link ? "external" : (tt.data.types.length ? "angolauk" : "none"));
      setForm({ ...e, ticketing_choice: choice });
      setTk({ choice, capacity: tt.data.capacity || "", end_time: tt.data.end_time || "", end_date: tt.data.end_date || "",
        types: tt.data.types.map((t) => ({ ...blankType(t.modality), ...t, sales_start: toLocalInput(t.sales_start), sales_end: toLocalInput(t.sales_end) })) });
    }).catch(() => { toast.error("Não foi possível carregar o evento"); nav("/eventos/organizador"); });
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeType = (id) => {
    setType(id);
    setForm(id === "event" ? { format: "in_person", timezone: "Europe/London" } : {});
  };

  const successMessages = {
    event: "Evento submetido para aprovação.",
    job: "Vaga submetida para aprovação.",
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const cfg = TYPES.find((x) => x.id === type);
    try {
      if (type === "event") {
        if (tk.choice === "angolauk" && tk.types.length === 0) { toast.error("Adiciona pelo menos um tipo de bilhete"); setBusy(false); return; }
        const body = { ...form, ticketing_choice: tk.choice, ticket_link: tk.choice === "external" ? form.ticket_link : null };
        const r = editId ? await api.put(`/events/${editId}`, body) : await api.post("/events", body);
        if (tk.choice === "angolauk" || editId) await api.put(`/events/${r.data.id}/tickets`, toTicketingBody(tk, body.format));
        toast.success(editId ? "Evento actualizado" : tk.choice === "angolauk" ? "Evento submetido para aprovação. As reservas abrem quando for aprovado." : successMessages.event);
        nav("/eventos/organizador"); return;
      }
      await api.post(cfg.endpoint, form);
      toast.success(successMessages[type]);
      setForm({});
      nav("/perfil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-4">
        <h1 className="font-display text-2xl font-extrabold">{editId ? "Editar evento" : t("publish")}</h1>
        <p className="text-sm text-zinc-500">Todas as submissões passam por moderação.</p>
      </div>

      {!editId && <div className="grid grid-cols-4 gap-2 mb-5">
        {TYPES.map(({ id, label, icon: I }) => (
          <button
            key={id}
            type="button"
            onClick={() => changeType(id)}
            data-testid={`publish-type-${id}`}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-colors ${
              type === id ? "bg-black text-white border-black" : "bg-white border-zinc-200 hover:border-black"
            }`}
          >
            <I size={18} />
            <span className="text-[11px] font-semibold">{label}</span>
          </button>
        ))}
      </div>}

      {form === null ? <div className="h-40 bg-white rounded-2xl border border-zinc-200 animate-pulse" /> : (
      <form onSubmit={submit} className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-4 mb-6 pb-6">
        {type === "event" && (
          <>
            <Field label="Nome do evento" required>
              <TextInput testid="pub-event-name" value={form.name} onChange={(v) => set("name", v)} required />
            </Field>
            <Field label="Categoria" required>
              <SelectInput testid="pub-event-category" value={form.category} onChange={(v) => set("category", v)} options={form.category && !EVENT_CATEGORIES.includes(form.category) ? [form.category, ...EVENT_CATEGORIES] : EVENT_CATEGORIES} required />
            </Field>
            <Field label="Descrição" required>
              <TextArea testid="pub-event-description" value={form.description} onChange={(v) => set("description", v)} required />
            </Field>
            <EventFormatFields form={form} set={set} />
            <Field label="Organizador" required>
              <TextInput testid="pub-event-organiser" value={form.organiser} onChange={(v) => set("organiser", v)} required />
            </Field>
            <Field label="Contacto (telefone ou email)">
              <TextInput testid="pub-event-contact" value={form.contact} onChange={(v) => set("contact", v)} placeholder="Opcional" />
            </Field>
            <TicketingSection tk={tk} setTk={setTk} form={form} set={set} format={form.format || "in_person"} />
            <Field label="Imagem">
              <ImageUploader testid="pub-event-image" value={form.image_url} onChange={(v) => set("image_url", v)} />
            </Field>
          </>
        )}

        {type === "job" && (
          <>
            <Field label="Título da vaga" required>
              <TextInput testid="pub-job-title" value={form.title} onChange={(v) => set("title", v)} required />
            </Field>
            <Field label="Empresa" required>
              <TextInput testid="pub-job-company" value={form.company} onChange={(v) => set("company", v)} required />
            </Field>
            <Field label="Cidade" required>
              <TextInput testid="pub-job-city" value={form.city} onChange={(v) => set("city", v)} placeholder="Ex: London" required />
            </Field>
            <Field label="Categoria" required>
              <SelectInput testid="pub-job-category" value={form.category} onChange={(v) => set("category", v)} options={JOB_CATEGORIES} required />
            </Field>
            <Field label="Descrição" required>
              <TextArea testid="pub-job-description" value={form.description} onChange={(v) => set("description", v)} required />
            </Field>
            <Field label="Salário">
              <TextInput testid="pub-job-salary" value={form.salary} onChange={(v) => set("salary", v)} placeholder="Opcional" />
            </Field>
            <Field label="Contacto">
              <TextInput testid="pub-job-contact" value={form.contact} onChange={(v) => set("contact", v)} placeholder="Email ou telefone" />
            </Field>
            <Field label="Link de candidatura">
              <TextInput testid="pub-job-apply" type="url" value={form.apply_link} onChange={(v) => set("apply_link", v)} placeholder="https://... (opcional)" />
            </Field>
          </>
        )}

        <button
          data-testid="publish-submit"
          type="submit"
          disabled={busy}
          className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white font-semibold transition-colors"
        >
          {busy ? "A enviar..." : editId ? "Guardar alterações" : "Submeter para aprovação"}
        </button>
        {editId && form.id && (
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-zinc-100" data-testid="publish-danger-zone">
            <p className="text-[11px] text-zinc-500 min-w-0">Sem reservas, o evento é removido da plataforma. Com reservas, é cancelado e os bilhetes ficam preservados.</p>
            <DeleteEventButton e={form} className="shrink-0" onDeleted={() => nav("/eventos/organizador")} onCancelled={() => nav("/eventos/organizador")} />
          </div>
        )}
      </form>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
        {label}{required && <span className="text-[#c8102e] ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

function TextInput({ testid, value, onChange, type = "text", placeholder = "", required = false }) {
  return (
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-3 rounded-lg border border-zinc-200 bg-zinc-50 text-sm outline-none focus:border-black focus:bg-white transition-colors min-h-[44px]"
    />
  );
}

function TextArea({ testid, value, onChange, required = false }) {
  return (
    <textarea
      data-testid={testid}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      className="w-full px-3 py-3 rounded-lg border border-zinc-200 bg-zinc-50 text-sm outline-none focus:border-black focus:bg-white resize-none transition-colors"
    />
  );
}

function SelectInput({ testid, value, onChange, options, required = false }) {
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-3 py-3 rounded-lg border border-zinc-200 bg-zinc-50 text-sm outline-none focus:border-black min-h-[44px]"
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export function ImageUploader({ testid, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pickNative = async () => {
    try {
      const file = await pickImageFile();
      await uploadFile(file);
    } catch (err) {
      if (!/cancel/i.test(String(err?.message))) toast.error("Não foi possível aceder à câmara/galeria");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Apenas imagens são permitidas");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${API}/files/${data.file_id}`;
      onChange(url);
      toast.success("Imagem carregada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro no upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        data-testid={`${testid}-file`}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
      />
      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50">
          <img src={value} alt="Pré-visualização" className="w-full aspect-video object-cover" />
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1.5"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testid}-upload-btn`}
          onClick={() => (isNative ? pickNative() : inputRef.current?.click())}
          disabled={uploading}
          className="w-full flex flex-col items-center justify-center gap-1 py-6 rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 hover:border-black hover:bg-white text-zinc-500 hover:text-black transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <span className="text-sm">A carregar...</span>
          ) : (
            <>
              <Upload size={20} />
              <span className="text-xs font-semibold">Carregar imagem</span>
              <span className="text-[10px] text-zinc-400">JPG, PNG, WebP · máx. 8MB</span>
            </>
          )}
        </button>
      )}
      <div className="flex items-center gap-2 text-[10px] text-zinc-400 uppercase tracking-wider">
        <span className="flex-1 h-px bg-zinc-200" />ou<span className="flex-1 h-px bg-zinc-200" />
      </div>
      <div className="relative">
        <ImageIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid={`${testid}-url`}
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Colar URL de imagem"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-zinc-200 bg-zinc-50 text-xs outline-none focus:border-black focus:bg-white transition-colors"
        />
      </div>
    </div>
  );
}
